'use client';

import { useState } from 'react';
import { Button } from '../../atoms/button/button';
import { ConfirmationDialog } from '../../molecules/confirmation-dialog/confirmation-dialog';
import { DetailField, DetailSelectInput } from '../../molecules/detail-fields/detail-fields';

export type DemoMeterLayer = 'gse' | 'bolletta' | 'meter' | 'tipico' | 'all';

export interface DemoMeterTarget {
	id: string;
	label: string;
	kind: 'M1' | 'M2' | 'M3';
}

export interface DemoMeterDataViewProps {
	enabled: boolean;
	loading: boolean;
	error: string | null;
	targets: DemoMeterTarget[];
	selectedId: string;
	onSelect: (id: string) => void;
	busy: boolean;
	onGenerate: (layer: DemoMeterLayer) => void;
	result: string | null;
}

export function DemoMeterDataView({
	enabled,
	loading,
	error,
	targets,
	selectedId,
	onSelect,
	busy,
	onGenerate,
	result,
}: DemoMeterDataViewProps) {
	const [layer, setLayer] = useState<DemoMeterLayer>('all');
	const [confirm, setConfirm] = useState(false);
	const selected = targets.find((t) => t.id === selectedId);
	const layers = [
		{ value: 'all', label: 'Tutti i livelli' },
		{ value: 'gse', label: 'Dati GSE' },
		{ value: 'bolletta', label: 'Bollette' },
		{ value: 'meter', label: 'Letture contatore' },
		...(selected?.kind === 'M3' ? [] : [{ value: 'tipico', label: 'Curva tipica' }]),
	];
	const activeLayer = layer === 'tipico' && selected?.kind === 'M3' ? 'all' : layer;
	return (
		<section
			aria-label="Dati demo dei misuratori"
			className="space-y-4 rounded-xl border border-border bg-card p-4 sm:p-5"
		>
			<h2 className="text-base font-semibold text-text">Dati demo dei misuratori</h2>
			{!enabled ? (
				<p className="text-sm text-text-muted">
					Seleziona una configurazione demo per generare i dati di M1, M2 e M3.
				</p>
			) : loading ? (
				<p className="text-sm text-text-muted">Caricamento dei punti di misura…</p>
			) : (
				<>
					<p className="text-sm text-text-muted">
						Genera un anno di dati demo per il punto di misura selezionato.
					</p>
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
						<DetailField label="Socio e punto di misura">
							<DetailSelectInput
								value={selectedId}
								onChange={onSelect}
								options={targets.map((t) => ({ value: t.id, label: t.label }))}
								placeholder={
									targets.length ? 'Seleziona M1, M2 o M3' : 'Nessun punto di misura disponibile'
								}
								disabled={busy || targets.length === 0}
							/>
						</DetailField>
						<DetailField label="Dati da generare">
							<DetailSelectInput
								value={activeLayer}
								onChange={(v) => setLayer(v as DemoMeterLayer)}
								options={layers}
								disabled={busy || !selected}
							/>
						</DetailField>
					</div>
					<div className="flex flex-wrap items-center justify-between gap-3">
						{result && <output className="text-sm text-text">{result}</output>}
						<Button
							type="button"
							variant="accent"
							size="sm"
							onClick={() => setConfirm(true)}
							disabled={busy || !selected}
						>
							{busy ? 'Generazione…' : 'Genera dati demo'}
						</Button>
					</div>
				</>
			)}
			{error && (
				<p role="alert" className="text-sm text-danger">
					{error}
				</p>
			)}
			<ConfirmationDialog
				open={confirm}
				onOpenChange={setConfirm}
				title="Generare i dati demo?"
				description={`Sovrascrive i dati demo del livello selezionato per ${selected?.label ?? 'il punto selezionato'} con un anno di dati sintetici.`}
				confirmText="Genera"
				cancelText="Annulla"
				onConfirm={() => onGenerate(activeLayer)}
			/>
		</section>
	);
}
